import '../App.css';

import navStyles from './CaseNav.module.css';
import { useState, useEffect, useRef } from 'react';
import { logPageView, logTrace, onLogout } from '../utils/trackingUtils';

import CaseNav from './CaseNav';
import CaseEfficiency from './CaseViewer/CaseEfficiency';
import CannulationTechnique from './CaseViewer/CannulationTechnique';
import AchievedCorrection from './Alignment/AchievedCorrection';
import logoMobile from '../assets/logo-mobile.png';

import Recording from './CaseViewer/Recording';
import { CaseReport, CaseReportSummary } from '../models/CaseTypes';
import { formatCaseDateDisplay } from '../utils/caseUtils';
import { sortCases } from '../utils/caseUtils';
import CaseList from './CaseList';
import ICaseReportService from '../services/ICaseReportService';

interface PortalLayoutProps {
  caseName?: string;
  caseReportService: ICaseReportService;
  logout: () => Promise<void>;
  navigate: (loc: string) => void;
}

const PortalLayout = (
  { caseName, caseReportService, logout, navigate }: PortalLayoutProps
): React.JSX.Element => {

  const [activeTab, setActiveTab] = useState<string>('');
  const [caseDetailLoadFailed, setCaseDetailLoadFailed] = useState<boolean>(false);
  const [cases, setCases] = useState<CaseReportSummary[]|null>(null);
  const [doLoad, setDoLoad] = useState<boolean>(true);
  const [error, setError] = useState<string>('');
  const [isDragging, setIsDragging] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [scrollLeft, setScrollLeft] = useState<number>(0);
  const [selectedCase, setSelectedCase] = useState<CaseReport|null>(null);
  const [sortField, setSortField] = useState<string>('date');
  const [sortDirection, setSortDirection] = useState<string>('desc');
  const [startX, setStartX] = useState<number>(0);

  const tabsRef = useRef<HTMLDivElement>(null);

  enum TabNames {
    EFFICIENCY = 'efficiency',
    CANNULATION = 'cannulation',
    CORRECTION = 'correction',
    RECORDING = 'recording'
  }

  const fetchCases = async (): Promise<void> => {
    try {
      setDoLoad(false); // Prevent multiple loads, unless requested.
      setLoading(true);
      setError('');
      const cases = await caseReportService.getCaseSummaries();
      sortCases(cases, sortField, sortDirection);
      setCases(cases);
    } catch (err) {
      console.error('Error fetching cases:', err);
      setError('Failed to load cases. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const fetchCaseDetail = async (caseId: string): Promise<CaseReport|null> => {
    let caseDetail = null;
    try {
      setLoading(true);
      setError('');
      caseDetail = await caseReportService.getCaseDetail(caseId);
    } catch (err) {
      console.error('Error fetching case detail:', err);
      setError('Failed to load case detail. Please try again.');
      setCaseDetailLoadFailed(true);
    } finally {
      setLoading(false);
    }
    return caseDetail;
  };

  const handleCaseSelect = async (caseSummary: CaseReportSummary): Promise<void> => {
    navigate(`/cases/${caseSummary.name}`);
    loadCase(caseSummary.id);
  };

  const loadCase = async (caseId: string): Promise<void> => {
    const caseDetail = await fetchCaseDetail(caseId);
    if (!caseDetail) {
      navigate('/cases');
      return;
    }
    setSelectedCase(caseDetail);

    if (caseDetail.hasRecording && !caseDetail.hasActivities) {
      setActiveTab(TabNames.RECORDING);
    } else {
      setActiveTab(TabNames.EFFICIENCY);
    }
  };

  const handleLogout = async (): Promise<void> => {
    caseReportService.clearCache();
    try {
      onLogout();
      await logout();
      navigate('/login');
    } catch (error) {
      console.error('Failed to log out', error);
    }
  };

  const handleMouseDown = (e: React.MouseEvent): void => {
    if (window.innerWidth > 390) return; // Only enable on mobile
    setIsDragging(true);
    const slider = tabsRef.current;
    if (!slider) return;
    setStartX(e.pageX);
    setScrollLeft(slider.scrollLeft);
  };

  const handleMouseMove = (e: React.MouseEvent): void => {
    if (window.innerWidth > 390 || !isDragging || !tabsRef.current) return;

    e.preventDefault();
    const x = e.pageX;
    const scrollSpeed = 1; // Adjust this value to control scroll speed (0.5 = slower, 2 = faster)
    const walk = (startX - x) * scrollSpeed; // Multiply the movement by scroll speed
    tabsRef.current.scrollLeft = scrollLeft + walk;
  };

  const handleMouseUpOrLeave = (): void => {
    setIsDragging(false);
  };

  const handleRetryFetch = (): void => {
    fetchCases();
  };

  const handleSort = (field: string): void => {
    if (cases === null) return;

    const newDirection = sortField === field && sortDirection === 'asc' ? 'desc' : 'asc';
    logTrace('CaseList', 'proprio_case_sort_' + field + ' ' + newDirection, field + ' ' + newDirection);
    setSortField(field);
    setSortDirection(newDirection);
    const sortedCases = sortCases(cases, field, newDirection);
    setCases(sortedCases);
  };

  const handleTabChange = (tab: string): void => {
    if (isDragging) return; // Prevent tab change while dragging
    logTrace('CaseDetail', 'proprio_tab_change_' + tab, tab);
    setActiveTab(tab);
  };

  // After loading case summaries, load the case detail for specified (or default) case.
  useEffect(() => {
    if (cases && cases.length > 0 && !loading && !selectedCase && !caseDetailLoadFailed) {
      // Check if the case exists in the loaded cases
      const existingCase = cases.find(c => c.name === caseName);
      if (existingCase) {
        loadCase(existingCase.id);
      } else {
        loadCase(cases[0].id);
      }
    }
  }, [caseName, cases, loadCase, loading, selectedCase, caseDetailLoadFailed]);

  useEffect(() => {
    if (!selectedCase || !activeTab) {
      return;
    }

    logPageView('/cases/' + selectedCase.name + '/' + activeTab);
  }, [selectedCase, activeTab]);

  // Load all cases for user on initial render
  useEffect(() => {
    if (doLoad && cases === null && !loading) {
      fetchCases();
    }
  }, [cases, doLoad, loading, fetchCases]);

  const hasActivities = selectedCase?.hasActivities;
  const hasAlignment = selectedCase?.alignment?.metric_sessions?.length || 0 > 0;

  if (loading) {
    return <div className="loading-indicator">Loading...</div>;
  }

  document.documentElement.style.setProperty('--mobile-logo', `url(${logoMobile})`);

  return (
    <div className={navStyles['portal-window']}>

      {/* Header navigation */}
      <CaseNav
        error={error}
        onLogout={handleLogout}
        onCaseSelect={handleCaseSelect}
        onReload={handleRetryFetch}
        onSortCases={(handleSort)}
        sortedCases={cases}
        sortField={sortField}
        sortDirection={sortDirection}
      />

      {/* Main Content */}
      <div className={navStyles['portal-content']}>

        {/* Case History Sidebar */}
        <CaseList
          cases={cases}
          selectedCase={selectedCase}
          onCaseSelect={handleCaseSelect}
          onReload={handleRetryFetch}
          onSortCases={handleSort}
          error={error}
          sortField={sortField}
          sortDirection={sortDirection}
        />

        {/* Case Detail View */}
        {selectedCase ? (
          <div className={navStyles['case-detail-container']}>
            <div className={navStyles['case-detail-header']}>
              <div className={navStyles['case-detail-info']}>
                <div className={navStyles['case-title-section']}>
                  <div>
                    <div className={navStyles['case-title']}>Case Performance Report</div>
                  </div>

                  <div className={navStyles['case-metadata']}>
                    <div className={navStyles['metadata-group']}>
                      <div className={navStyles['metadata-label']}>Case Name</div>
                      <div className={navStyles['metadata-value']}>{selectedCase.name}</div>
                    </div>

                    <div className={navStyles['metadata-group']}>
                      <div className={navStyles['metadata-label']}>Case Date</div>
                      <div className={navStyles['metadata-value']}>{formatCaseDateDisplay(selectedCase.date)}</div>
                    </div>

                    <div className={navStyles['metadata-group']}>
                      <div className={navStyles['metadata-label']}>Navigated Levels</div>
                      <div className={navStyles['metadata-value']}>{selectedCase.levels}</div>
                    </div>

                  </div>
                </div>

                <div
                  ref={tabsRef}
                  className={navStyles['nav-tabs']}
                  onMouseDown={handleMouseDown}
                  onMouseMove={handleMouseMove}
                  onMouseUp={handleMouseUpOrLeave}
                  onMouseLeave={handleMouseUpOrLeave}
                >
                  <div
                    data-testid="tab-efficiency"
                    className={`${navStyles['nav-tab']} ${!hasActivities && navStyles['nav-tab-disabled']} ${activeTab === TabNames.EFFICIENCY ? navStyles['nav-tab-active'] : ''}`}
                    onClick={() => handleTabChange(TabNames.EFFICIENCY)}
                  >
                    <span className={`${navStyles['nav-tab-text']} ${activeTab === TabNames.EFFICIENCY ? navStyles['nav-tab-text-active'] : ''}`}>
                      Case Efficiency
                    </span>
                  </div>
                  <div
                    data-testid="tab-cannulation"
                    className={`${navStyles['nav-tab']} ${!hasActivities && navStyles['nav-tab-disabled']} ${activeTab === TabNames.CANNULATION ? navStyles['nav-tab-active'] : ''}`}
                    onClick={() => handleTabChange(TabNames.CANNULATION)}
                  >
                    <span className={`${navStyles['nav-tab-text']} ${activeTab === TabNames.CANNULATION ? navStyles['nav-tab-text-active'] : ''}`}>
                      Cannulation Technique
                    </span>
                  </div>

                  <div
                    data-testid="tab-correction"
                    className={`${navStyles['nav-tab']} ${!hasAlignment && navStyles['nav-tab-disabled']} ${activeTab === TabNames.CORRECTION ? navStyles['nav-tab-active'] : ''}`}
                    onClick={() => handleTabChange(TabNames.CORRECTION)}
                  >
                    <span className={`${navStyles['nav-tab-text']} ${activeTab === TabNames.CORRECTION ? navStyles['nav-tab-text-active'] : ''}`}>
                      Alignment
                    </span>
                  </div>

                  <div
                    data-testid="tab-recording"
                    className={`${navStyles['nav-tab']} ${!selectedCase?.hasRecording && navStyles['nav-tab-disabled']} ${activeTab === TabNames.RECORDING ? navStyles['nav-tab-active'] : ''}`}
                    onClick={() => handleTabChange(TabNames.RECORDING)}
                  >
                    <span className={`${navStyles['nav-tab-text']} ${activeTab === TabNames.RECORDING ? navStyles['nav-tab-text-active'] : ''}`}>
                      Recording
                    </span>
                  </div>
                </div>
              </div>
            </div>

            <div className={navStyles['case-content']}>
              {activeTab === TabNames.EFFICIENCY && (
                <CaseEfficiency caseReport={selectedCase} />
              )}

              {activeTab === TabNames.CANNULATION && (
                <CannulationTechnique caseReport={selectedCase} />
              )}

              {activeTab === TabNames.CORRECTION && (
                <AchievedCorrection caseReport={selectedCase} />
              )}

              {activeTab === TabNames.RECORDING && (
                <Recording caseReport={selectedCase} />
              )}
            </div>
          </div>
        ) : error ? (
          <div className={navStyles['no-case-selected']}>
            <p>{error}</p>
          </div>
        ) : 
          (
            <div className={navStyles['no-case-selected']}>
              <p>Select a case to view details</p>
            </div>
          )}
      </div>
    </div>
  );
};

export default PortalLayout;
